import { useEffect, useState } from "react";
import { get as _get } from "lodash";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { BigNumber } from "bignumber.js";
import { utils as koilibUtils } from "koilib";
import { useSnackbar } from "notistack";

// components mui
import {
  Alert,
  Box,
  Card,
  Slider,
  CardContent,
  Typography,
  IconButton,
  InputBase,
  Button,
  CircularProgress,
  Tooltip,
  CardHeader,
} from "@mui/material";

// icons
import SettingsIcon from "@mui/icons-material/Settings";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { invalidateAfterTransaction } from "../../helpers/queryClient";
import { useWallet } from "../../hooks/useWallet";

// components
import Maintenance from "../../components/Maintenance";
import TxSnackbarMessage from "../../components/TxSnackbarMessage";
import DetailRow from "../../components/DetailRow";
import { formatAmount } from "../../utils/format";
import { amountType } from "../../theme/main";
import { getErrorMessage } from "../../utils/errors";

import { numberPattern } from "../../utils/regex";

// Actions
import { setModalData, setModal } from "../../redux/actions/modals";

// constants
import { CONFIG_BASE } from "../../constants/configs";

// contracts
import { PeripheryContract, CoreContract, TokenContract } from "../../helpers/contracts";

// helpers
import { orderTokens, calculateDefaultMana } from "../../helpers/calculate";

// utils
import { loadTokenData } from "../../utils/token";
import { waitTransation, describeWaitError } from "./../../utils/transactions";

const RemoveLiquidityPage = () => {
  // Dispatch to call actions
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const Snackbar = useSnackbar();

  // selectors
  const walletSelector = useWallet();
  const settingsSelector = useSelector((state) => state.settings);
  const base_tokens = useSelector((state) => state.tokens.base_tokens);
  const custom_tokens = useSelector((state) => state.tokens.custom_tokens);

  // state
  const [percent, setPercent] = useState(0);
  const [position, setPosition] = useState({});
  // true until the position is loaded
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState(null);

  // variables
  const slippage = _get(settingsSelector, "slippage", "0.1");
  const provider = _get(walletSelector, "provider", null);
  const signer = _get(walletSelector, "signer", null);

  // functions
  const loadPools = async () => {
    setLoading(true);
    const ContractProtocol = PeripheryContract(provider, signer);
    let functions = ContractProtocol.functions;
    try {
      let address = _get(walletSelector, "wallet[0].address", "");
      let _tokens = orderTokens({ address: _get(params, "token_a", "") }, { address: _get(params, "token_b", "") });
      // process
      let pairTokens = {
        tokenA: _get(_tokens, "token0.address", ""),
        tokenB: _get(_tokens, "token1.address", ""),
      };
      let pairAddress = (await functions.get_pair(pairTokens)).result;
      if (pairAddress) {
        let pair = CoreContract(pairAddress.value, provider, signer);
        let balance = (await pair.functions.balance_of({ owner: address })).result;
        if (_get(balance, "value", "0") != "0") {
          let supply = (await pair.functions.total_supply()).result;
          let reserves = (await pair.functions.get_reserves()).result;
          // find tokenA
          let tokenAData = base_tokens
            .concat(custom_tokens)
            .find((token) => token.address === _get(_tokens, "token0.address", ""));
          if (!tokenAData) {
            tokenAData = await loadTokenData(_get(_tokens, "token0.address", ""));
          }
          // find tokenB
          let tokenBData = base_tokens
            .concat(custom_tokens)
            .find((token) => token.address === _get(_tokens, "token1.address", ""));
          if (!tokenBData) {
            tokenBData = await loadTokenData(_get(_tokens, "token1.address", ""));
          }
          setPosition({
            lpAddress: pairAddress.value,
            owner: address,
            totalSupply: _get(supply, "value", "0"),
            shares: _get(balance, "value", "0"),
            reserves: reserves,
            tokenA: tokenAData,
            tokenB: tokenBData,
          });
        } else {
          throw "Not enough liquidity";
        }
      } else {
        throw "Pair does not exist";
      }
    } catch (error) {
      console.log(error);
      setErrors(error);
    }
    setLoading(false);
  };
  const closeWallet = () => {
    setPosition({});
  };
  const backPage = () => {
    navigate("/liquidity");
  };
  const openModalSettingsApp = (data) => {
    dispatch(setModalData(data));
    dispatch(setModal("SettingsApp"));
  };
  const calculatePercent = (_position, typed) => {
    let reserveA = _get(_position, "reserves.reserveA", "");
    let reserveB = _get(_position, "reserves.reserveB", "");
    let shares = _get(_position, "shares", "");
    let total = _get(_position, "totalSupply", "");
    let token = typed == "A" ? _get(_position, "tokenA", {}) : _get(_position, "tokenB", {});
    let liquidityPercent = new BigNumber(shares).times(percent / 100).toFixed(0, 1);
    let amountRemove = new BigNumber(liquidityPercent)
      .times(typed == "A" ? reserveA : reserveB)
      .div(total)
      .toFixed(0, 1);
    let slippageRemoved = new BigNumber(amountRemove).times(slippage / 100).toFixed(0, 1);
    let result = new BigNumber(amountRemove).minus(slippageRemoved).toFixed(0, 1);
    return token.decimals != "0" ? koilibUtils.formatUnits(result, token.decimals) : result;
  };

  const approvalAllowance = async (tokenLPAddress, ownerAddress, spenderAddress, amount, configs) => {
    let _token = TokenContract(tokenLPAddress, provider, signer);
    _token.options.onlyOperation = true;
    const approvalParams = {
      owner: ownerAddress,
      spender: spenderAddress,
      value: amount,
    };
    let { operation: approveSales } = await _token.functions.approve(approvalParams, configs);
    return approveSales;
  };

  const getConfigs = async (_walletAddress) => {
    let configs = {
      payer: _walletAddress,
    };
    try {
      let account_rc = await provider.getAccountRc(_walletAddress);
      let rc_limit = calculateDefaultMana(account_rc);
      configs.rc_limit = rc_limit;
    } catch (error) {
      console.log(error);
      setLoading(false);
      Snackbar.enqueueSnackbar("Error in mana calculation", { variant: "error" });
      return;
    }
    return configs;
  };

  const finalRemove = async () => {
    setLoading(true);
    // config contract
    const ContractProtocol = PeripheryContract(provider, signer);
    ContractProtocol.options.onlyOperation = true;
    let functions = ContractProtocol.functions;
    let walletAddress = signer.getAddress();

    // pre data
    let lpAddress = _get(position, "lpAddress", "");
    let shares = _get(position, "shares", "");
    let reserveA = _get(position, "reserves.reserveA", "");
    let reserveB = _get(position, "reserves.reserveB", "");
    let total = _get(position, "totalSupply", "");

    // calculate
    let liquidityToRemove = new BigNumber(shares).times(percent / 100).toFixed(0, 1);
    let preAmountAMin = new BigNumber(liquidityToRemove).times(reserveA).div(total).toFixed(0, 1);
    let preAmountBMin = new BigNumber(liquidityToRemove).times(reserveB).div(total).toFixed(0, 1);
    let amountAMin = new BigNumber(preAmountAMin)
      .minus(new BigNumber(preAmountAMin).times(slippage / 100).toFixed(0, 1))
      .toFixed(0, 1);
    let amountBMin = new BigNumber(preAmountBMin)
      .minus(new BigNumber(preAmountBMin).times(slippage / 100).toFixed(0, 1))
      .toFixed(0, 1);

    let configs = await getConfigs(walletAddress);
    let operations = [];
    // approve allowance of lp token
    try {
      let approvalTokenA = await approvalAllowance(
        lpAddress,
        walletAddress,
        ContractProtocol.getId(),
        liquidityToRemove,
        configs
      );
      if (approvalTokenA) {
        operations.push(approvalTokenA);
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
      Snackbar.enqueueSnackbar("Approval token a error", { variant: "error" });
      return;
    }

    // remove liquidity
    try {
      let _params = {
        from: walletAddress,
        receiver: walletAddress,
        tokenA: _get(params, "token_a", ""),
        tokenB: _get(params, "token_b", ""),
        liquidity: liquidityToRemove,
        amountAMin: amountAMin,
        amountBMin: amountBMin,
      };
      let { operation: operation } = await functions.remove_liquidity(_params, configs);
      operations.push(operation);
    } catch (error) {
      console.log(error);
      Snackbar.enqueueSnackbar("Error in remove liquidity", { variant: "error" });
      return;
    }

    let transaction = null;
    try {
      const tx = await signer.prepareTransaction({ operations: operations, header: configs });
      const { transaction: _transaction } = await signer.sendTransaction(tx);
      transaction = _transaction;

      Snackbar.enqueueSnackbar("Transaction sent", { variant: "info" });
    } catch (error) {
      console.log(error);
      Snackbar.enqueueSnackbar(getErrorMessage(error), { variant: "error" });
      setLoading(false);
      return;
    }

    // wait to be mined
    try {
      await waitTransation(provider, transaction);
    } catch (error) {
      console.log(error);
      const { message, variant } = describeWaitError(error);
      Snackbar.enqueueSnackbar(<TxSnackbarMessage message={message} txId={transaction.id} />, {
        variant,
        autoHideDuration: 15000,
      });
      setLoading(false);
      return;
    }
    setLoading(false);
    Snackbar.enqueueSnackbar(<TxSnackbarMessage message="Removed liquidity" txId={transaction.id} />, {
      variant: "success",
      autoHideDuration: 10000,
    });
    invalidateAfterTransaction();
    setTimeout(() => navigate("/liquidity"), 2000);
  };

  // functions component
  const disabledButton = () => {
    if (percent <= 0) return true;
    return false;
  };
  const messageButton = () => {
    if (percent <= 0) return "Liquidity to remove is very small";
    return "Remove";
  };

  // effects
  // the token list is needed first: "koin" in the url is a name, read as a contract it fails
  const tokensLoaded = base_tokens.length + custom_tokens.length > 0;
  useEffect(() => {
    if (_get(walletSelector, "wallet", null)) {
      if (tokensLoaded) loadPools();
    } else {
      closeWallet();
    }
  }, [_get(walletSelector, "wallet[0].address", ""), tokensLoaded]);

  // components
  const Loading = () => (
    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
      <CircularProgress aria-label="loading the position" />
    </Box>
  );

  if (!_get(CONFIG_BASE, "contracts.periphery.launched", false)) {
    return <Maintenance />;
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "center", pb: { xs: 10, md: 6 } }}>
      <Card sx={{ maxWidth: 480, width: "100%" }}>
        <CardHeader
          avatar={
            <IconButton aria-label="back to liquidity" edge="start" onClick={() => backPage()}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          }
          title="Remove liquidity"
          action={
            <Tooltip title="Settings">
              <IconButton aria-label="settings" onClick={() => openModalSettingsApp()}>
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          }
          sx={{ "& .MuiCardHeader-avatar": { mr: 1 } }}
        />
        <CardContent>
          {_get(walletSelector, "wallet", null) && !errors ? (
            !loading ? (
              <Box>
                {/* the share of the position to withdraw */}
                <Box sx={{ p: 2, borderRadius: "14px", backgroundColor: "background.light" }}>
                  <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                    Amount to remove
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                    <InputBase
                      placeholder="0"
                      type="text"
                      inputMode="decimal"
                      value={percent}
                      slotProps={{ input: { "aria-label": "percent of the position to remove" } }}
                      sx={{
                        ...amountType,
                        fontSize: "2.5rem",
                        // as wide as the digits typed, so the % sign follows them
                        width: `${Math.max(String(percent || "").length, 1) + 0.4}ch`,
                        color: "text.primary",
                      }}
                      onChange={(e) => numberPattern(e.target.value, setPercent)}
                    />
                    <Typography sx={{ ...amountType, fontSize: "1.75rem", color: "text.secondary" }}>%</Typography>
                  </Box>
                  <Slider
                    value={Number(percent) || 0}
                    min={0}
                    max={100}
                    aria-label="percent of the position to remove"
                    sx={{ mt: 1 }}
                    onChange={(e) => setPercent(e.target.value)}
                    valueLabelDisplay="auto"
                  />
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1 }}>
                    {[25, 50, 75, 100].map((value) => (
                      <Button
                        key={value}
                        size="small"
                        variant={percent == value ? "contained" : "outlined"}
                        onClick={() => setPercent(value)}
                      >
                        {value === 100 ? "Max" : `${value}%`}
                      </Button>
                    ))}
                  </Box>
                </Box>

                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  sx={{ mt: 2 }}
                  onClick={() => finalRemove()}
                  disabled={disabledButton()}
                >
                  {loading ? <CircularProgress color="inherit" size={18} sx={{ mr: 1.25 }} /> : null}
                  {messageButton()}
                </Button>

                {percent && Object.keys(position).length ? (
                  <Box
                    sx={{
                      mt: 2,
                      pt: 2,
                      borderTop: 1,
                      borderColor: "divider",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <DetailRow
                      label={
                        <Tooltip
                          placement="top"
                          title="How much the pool price may move against you before the withdrawal reverts. Change it in settings."
                        >
                          <span>Slippage tolerance</span>
                        </Tooltip>
                      }
                      value={`${slippage}%`}
                    />
                    <DetailRow
                      label={`Minimum ${_get(position, "tokenA.symbol", "")}`}
                      value={formatAmount(calculatePercent(position, "A"), { maxDecimals: 8 })}
                    />
                    <DetailRow
                      label={`Minimum ${_get(position, "tokenB.symbol", "")}`}
                      value={formatAmount(calculatePercent(position, "B"), { maxDecimals: 8 })}
                    />
                  </Box>
                ) : null}
              </Box>
            ) : (
              <Loading />
            )
          ) : null}

          {errors ? (
            <Alert variant="outlined" severity="info">
              {errors === "Not enough liquidity"
                ? "This wallet has no liquidity in this pool."
                : errors === "Pair does not exist"
                  ? "This pool does not exist."
                  : "Could not load this position. Try again in a moment."}
            </Alert>
          ) : null}

          {!_get(walletSelector, "wallet", null) ? (
            <Box>
              <Typography sx={{ color: "text.secondary", mb: 2 }}>
                Connect your wallet to remove liquidity from this pool.
              </Typography>
              <Button variant="contained" size="large" fullWidth onClick={() => dispatch(setModal("Connect"))}>
                Connect wallet
              </Button>
            </Box>
          ) : null}
        </CardContent>
      </Card>
    </Box>
  );
};

export default RemoveLiquidityPage;
